import { snackbarUtils } from '@ahamove/web-components/lib/SnackbarProvider';
import type { UseMutateFunction } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ContractRequestParams } from '@/api/contract/types';
import { usePostCreateContractMutation } from '@/api/contract/usePostCreateContract';
import type {
  EstimateFeeData,
  EstimateFeeParams,
  ServiceModalData,
} from '@/api/service/types';
import { useEstimateFeeMutation } from '@/api/service/useEstimateFee';
import { useGetServiceModelDetail } from '@/api/service/useGetServiceModelDetail';
import { useGetServiceModels } from '@/api/service/useGetServiceModels';
import type { ApiError, DefaultQueryError } from '@/api/types';
import type { FareType } from '@/global-stores/useDateTimeStore';
import { useDateTimeStore } from '@/global-stores/useDateTimeStore';
import { useEVStore } from '@/global-stores/useEVStore';
import { useLocationStore } from '@/global-stores/useLocationStore';
import { usePaymentMethodStore } from '@/global-stores/usePaymentMethodStore';
import { usePromoCodeStore } from '@/global-stores/usePromoCodeStore';
import { useRentTypeStore } from '@/global-stores/useRentTypeStore';
import apiRoutes from '@/lib/apiRoutes';
import { detectPlatform } from '@/lib/common';
import {
  ERROR_CODES,
  LOCAL_STORAGE_EV_BOOKING_NOTE,
  EV_TRACK_KEY,
  DEFAULT_DATE_TIME_FORMAT,
} from '@/lib/constants';
import { trackEvent } from '@/lib/eventTracking';
import { local } from '@/lib/storage';
import { convertBankCardNumber } from '@/lib/utils';
import { useAuth } from '@/modules/auth/AuthProvider';
import { checkoutConfig } from '@/modules/checkout/checkoutConfig';

export type ROImportType = 'automatic' | 'manual' | null;

export type AvailableEV = ServiceModalData & EstimateFeeData;

export type NewBookingContextProps = {
  activeStep: number;
  loading: boolean;
  serviceModels?: ServiceModalData[];
  setServiceModels?: React.Dispatch<React.SetStateAction<ServiceModalData[]>>;
  loadingServiceModels: boolean;
  // setLoadingServiceModels: React.Dispatch<React.SetStateAction<boolean>>;
  nextStep: (step?: number) => void;
  backStep: () => void;
  resetStep: () => void;
  goToCheckoutPage: () => void;
  availableEVs?: AvailableEV[];
  estimateFee: UseMutateFunction<
    EstimateFeeData[],
    DefaultQueryError,
    EstimateFeeParams,
    unknown
  >;
  errors: Record<string, string>;
  selectedEV?: AvailableEV;
  createContract: () => void;
  apiError: ApiError | null;
  estimateFeeError: ApiError | null;
  createdContract: boolean;
  scrollToTop: () => void;
  setOpenNoService: (value: boolean) => void;
  openNoService: boolean;
  openDialog: boolean;
  setOpenDialog: (value: boolean) => void;
  note: string;
  handleSaveNote: (value: string) => void;
  resetNote: () => void;
};

export const NewBookingContext = React.createContext<NewBookingContextProps>({
  activeStep: 0,
  loading: false,
  nextStep: () => {
    //
  },
  backStep: () => {
    //
  },
  resetStep: () => {
    //
  },
  goToCheckoutPage: () => {
    //
  },
  createContract: () => {
    //
  },
  scrollToTop: () => {
    //
  },
  apiError: null,
  createdContract: false,
} as unknown as NewBookingContextProps);

export const useNewBooking = () => React.useContext(NewBookingContext);

export const NewBookingProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  useRouter;
  const [activeStep, setActiveStep] = React.useState(0);
  const [errors, setErrorsState] = React.useState<
    {
      location?: string;
      time?: string;
      vehicle?: string;
    } & Record<string, string>
  >({
    location: 'Chọn địa điểm',
  });
  const [apiError, setApiError] = React.useState<ApiError | null>(null);
  const [createdContract, setCreatedContract] = React.useState<boolean>(false);
  const [openNoService, setOpenNoService] = React.useState(false);
  const { t } = useTranslation(checkoutConfig.i18nNamespaces);
  const { user, openRequiredAuthDialog } = useAuth();
  const { push, query, pathname, replace, locale } = useRouter();
  const { address, setAddress, resetLocationStore, city } = useLocationStore();
  const { rentType, setRentType } = useRentTypeStore();
  const { selectedEVId, setSelectedEVId } = useEVStore();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [note, setNote] = React.useState('');
  const {
    promoCodeStore,
    setPromoCodeStore,
    setErrorPromoCode,
    setIsForceClosePromoModal,
    resetPromoCodeStore,
  } = usePromoCodeStore();
  const {
    startTime,
    expectedEndTime,
    fareType,
    setStartTime,
    setExpectedEndTime,
    setFareType,
    resetDateTimeStore,
  } = useDateTimeStore();

  const { selectedPaymentMethod, resetSelectedPaymentMethodStore } =
    usePaymentMethodStore();

  const isCheckoutPage = pathname === '/checkout';
  const queryClient = useQueryClient();
  const { data: serviceModels, isFetching: loadingServiceModels } =
    useGetServiceModels(
      { lat: address?.lat as number, lng: address?.lng as number },
      {
        onSuccess: (data) => {
          const result = data.filter((item) => item?.service_type === rentType);
          queryClient.setQueryData(
            [
              apiRoutes.services.service_models,
              {
                lat: address?.lat as number,
                lng: address?.lng as number,
                rentType,
              },
            ],
            result
          );
          if (openDialog && (!data?.length || !result.length) && !!address) {
            setOpenNoService(true);
          }
        },
        cacheTime: 0,
        enabled: !!address && !isCheckoutPage,
      },
      { rentType }
    );

  const { data: serviceModelDetail, isFetching: loadingServiceModelDetail } =
    useGetServiceModelDetail(
      String(selectedEVId),
      { lat: address?.lat as number, lng: address?.lng as number },
      {
        enabled: !!selectedEVId && !!address && isCheckoutPage,
      }
    );

  const {
    mutate: estimateFee,
    isLoading: estimatingFeeLoading,
    data: estimateFeeResponse,
    error: estimateFeeError,
  } = useEstimateFeeMutation({});

  const { mutate: createContractMutate, isLoading: creatingContract } =
    usePostCreateContractMutation({});

  const createContract = () => {
    if (estimatingFeeLoading || loadingServiceModels || creatingContract)
      return;

    if (createdContract) return;

    if (!address) return;

    if (!selectedEV) return;

    if (!estimateFeeParams) return;

    setApiError(null);
    const payload: ContractRequestParams = {
      service_id: selectedEV.service_id,
      expected_rent_time: {
        start_time: estimateFeeParams.start_time,
        end_time: estimateFeeParams.expected_end_time,
      },
      promo_code: promoCodeStore || '',
      fare_type: estimateFeeParams.fare_type,
      // payment_method: selectedPaymentMethod?.code || 'cash',
      payment_method: selectedPaymentMethod?.extra_data?.card_number
        ? selectedPaymentMethod?.method_type
        : selectedPaymentMethod?.code || 'cash',
      estimated_fee: selectedEV.total_fee,
      pickup_location: [address.lng, address.lat],
      dropoff_location: [address.lng, address.lat],
      pickup_address: address.address,
      dropoff_address: address.address,
      payment_details: {
        method_id: selectedPaymentMethod?.method_id || 'PIUR',
        method_type: selectedPaymentMethod?.method_type || 'cash',
        method_code: selectedPaymentMethod?.code || 'cash',
        method_title:
          selectedPaymentMethod?.method_type === 'card'
            ? selectedPaymentMethod?.extra_data?.card_number &&
              convertBankCardNumber(
                selectedPaymentMethod.extra_data.card_number
              )
            : undefined,
      },
      note: note,
    };
    createContractMutate(payload, {
      onSuccess: (res) => {
        resetNote();
        local.removeItem(LOCAL_STORAGE_EV_BOOKING_NOTE);
        setCreatedContract(true);

        // Clear store
        resetLocationStore();
        resetDateTimeStore();
        resetPromoCodeStore();
        resetSelectedPaymentMethodStore();
        resetStep();
        setSelectedEVId(null);

        const user_agent = navigator.userAgent;
        const platform = detectPlatform(user_agent);
        trackEvent(EV_TRACK_KEY.ev_booking_confirmation_button, {
          user_id: user?._id || null,
          userId: user?._id || null,
          platform,
          user_agent,
          city_id: city,
          location: address?.address,
          start_time: dayjs.unix(startTime).format(DEFAULT_DATE_TIME_FORMAT),
          end_time: dayjs
            .unix(expectedEndTime)
            .format(DEFAULT_DATE_TIME_FORMAT),
          hour_renting: (expectedEndTime - startTime) / 3600,
          vehicle: selectedEVId,
          payment_method: selectedPaymentMethod
            ? selectedPaymentMethod.code
            : undefined,
          promo_code: promoCodeStore || undefined,
        });
        if (query.webinapp) {
          const queryReplace: Record<string, string> = {
            id: res._id,
          };
          queryReplace.webinapp = query.webinapp.toString();
          if (query.lat) queryReplace.lat = query.lat.toString();
          if (query.lng) queryReplace.lng = query.lng.toString();
          replace({
            pathname: '/bookings/[id]',
            query: {
              ...queryReplace,
            },
          }).finally(() => setCreatedContract(false));
        } else {
          replace({
            pathname: '/bookings/[id]',
            query: {
              id: res._id,
            },
          }).finally(() => setCreatedContract(false));
        }
      },
      onError: (err) => {
        if (
          (err as DefaultQueryError).response?.data.code ===
          ERROR_CODES.NOT_AUTHORIZED.code
        ) {
          openRequiredAuthDialog(true);
        }

        setApiError(err.response?.data || ERROR_CODES.INVALID_DATA);
      },
    });
  };

  const nextStep = (step?: number) => {
    if (!address) {
      setErrors('location');
      return;
    }

    if (!estimateFeeParams) {
      setErrors('time');
      return;
    }

    if (!availableEVs) {
      setErrors('vehicle');
    }

    if (step) {
      setActiveStep(step);
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const setErrors = (key: string) => {
    switch (key) {
      case 'location':
        setErrorsState((e) => ({ ...e, location: 'Chọn địa điểm' }));
        break;

      case 'time':
        setErrorsState((e) => ({ ...e, time: 'Chọn ngày & giờ' }));
        break;

      case 'vehicle':
        setErrorsState((e) => ({ ...e, vehicle: 'Không có xe khả dụng' }));
        break;

      default:
        break;
    }
  };

  const clearErrors = (key?: string) => {
    if (key) {
      setErrorsState((e) => {
        if (key in e) {
          delete e[key as keyof typeof e];
        }
        return e;
      });
      return;
    }

    setErrorsState({});
  };

  const estimateFeeParams: EstimateFeeParams | undefined = React.useMemo(() => {
    if (startTime <= 0 || expectedEndTime <= 0) return;
    clearErrors('time');

    const params = {} as EstimateFeeParams;

    params.start_time = startTime;
    params.expected_end_time = expectedEndTime;
    params.fare_type = fareType;
    params.lang = locale;

    if (isCheckoutPage) {
      if (!selectedEVId) return;

      if (promoCodeStore) {
        params.promo_code = promoCodeStore;
      }
      if (user && user._id) {
        params.client_id = user._id;
      }
      params.service_id = selectedEVId;

      return params;
    } else {
      if (address) {
        params.lat = address.lat;
        params.lng = address.lng;
      }
    }

    return params;
  }, [
    user,
    address,
    startTime,
    expectedEndTime,
    isCheckoutPage,
    fareType,
    promoCodeStore,
  ]);

  const handleSaveNote = (value: string) => {
    local.setItem(LOCAL_STORAGE_EV_BOOKING_NOTE, value);
    setNote(value);
  };

  const resetNote = () => {
    setNote('');
  };

  React.useEffect(() => {
    const { rentType: rentTypeUrl } = query;
    if (rentTypeUrl) {
      setRentType(rentTypeUrl);
    }
  }, []);

  React.useEffect(() => {
    const {
      address: addressQuery,
      lat,
      lng,
      start_time,
      expected_end_time,
      fare_type,
      service_id,
      promo_code,
    } = query;
    if (addressQuery && lat && lng) {
      setAddress({
        address: String(addressQuery),
        lat: Number(lat),
        lng: Number(lng),
      });
    }

    if (start_time) setStartTime(Number(start_time));
    if (expected_end_time) setExpectedEndTime(Number(expected_end_time));
    if (fare_type) setFareType(fare_type as FareType);
    if (promo_code) setPromoCodeStore(promo_code as string);
    if (service_id) {
      setSelectedEVId(String(service_id));

      if ((addressQuery && lat && lng) || address) {
        setActiveStep(1);
      } else {
        setActiveStep(0);
      }
    } else {
      setActiveStep(0);
      if (pathname === '/checkout') {
        push({ pathname: '/', query });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  React.useEffect(() => {
    setCreatedContract(false);
    setApiError(null);
  }, [pathname]);

  React.useEffect(() => {
    if (!address) {
      setErrors('location');
    } else clearErrors('location');
  }, [address]);

  React.useEffect(() => {
    if (estimateFeeParams && !estimatingFeeLoading) {
      console.log('Trigger call estimateFee with params', estimateFeeParams);
      if (!pathname.includes('bookings')) estimateFee(estimateFeeParams);
    }
  }, [estimateFee, estimateFeeParams]);

  React.useEffect(() => {
    const noteLocalStorage = localStorage.getItem('ev_booking_note');
    if (noteLocalStorage) {
      setNote(noteLocalStorage);
    }
  }, []);

  React.useEffect(() => {
    if (
      !estimateFeeResponse ||
      !estimateFeeParams?.promo_code ||
      estimatingFeeLoading ||
      !promoCodeStore ||
      pathname !== '/checkout'
    ) {
      return;
    }

    if (estimateFeeResponse[0].promo_code_invalid_reason.length > 0) {
      setErrorPromoCode(estimateFeeResponse[0].promo_code_invalid_reason);
      setIsForceClosePromoModal(false);
      // snackbarUtils.error(t('payment:promotion.apply_promo_code_error'));
    } else {
      setIsForceClosePromoModal(true);
      setErrorPromoCode('');
      const user_agent = navigator.userAgent;
      const platform = detectPlatform(user_agent);
      trackEvent(EV_TRACK_KEY.ev_promotion_use, {
        user_id: user?._id || null,
        userId: user?._id || null,
        platform,
        user_agent,
        city_id: city,
        location: address?.address,
        start_time: dayjs.unix(startTime).format(DEFAULT_DATE_TIME_FORMAT),
        end_time: dayjs.unix(expectedEndTime).format(DEFAULT_DATE_TIME_FORMAT),
        hour_renting: (expectedEndTime - startTime) / 3600,
        vehicle: selectedEVId,
        payment_method: selectedPaymentMethod
          ? selectedPaymentMethod.code
          : undefined,
        promo_code: promoCodeStore || undefined,
      });
      snackbarUtils.success(t('payment:promotion.apply_promo_code_success'));
    }
  }, [estimateFeeResponse]);

  const availableEVs: AvailableEV[] | undefined = React.useMemo(() => {
    if (!serviceModels || !estimateFeeResponse) return undefined;

    const mergedArr = serviceModels.reduce<AvailableEV[]>((acc, service) => {
      const estData = estimateFeeResponse.find(
        (o) => o.service_id === service._id
      );

      if (!estData) return [...acc];

      return [...acc, { ...service, ...estData }];
    }, []);

    if (mergedArr.length <= 0) return undefined;

    clearErrors('vehicle');
    return mergedArr;
  }, [serviceModels, estimateFeeResponse, rentType]);

  const selectedEV: AvailableEV | undefined = React.useMemo(() => {
    if (!selectedEVId) return undefined;

    if (availableEVs)
      return availableEVs.find((ev) => ev.service_id === selectedEVId);

    if (isCheckoutPage) {
      if (!estimateFeeResponse) return undefined;

      const res = estimateFeeResponse.find(
        (ev) => ev.service_id === selectedEVId
      );

      return (
        res
          ? serviceModelDetail
            ? { ...res, ...serviceModelDetail }
            : res
          : undefined
      ) as AvailableEV | undefined;
    }

    return undefined;
  }, [selectedEVId, availableEVs, estimateFeeResponse, serviceModelDetail]);

  const backStep = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const resetStep = () => {
    setActiveStep(0);
  };

  const goToCheckoutPage = () => {
    if (!address) return;
    if (!estimateFeeParams) return;
    if (!selectedEVId) return;
    if (!rentType) return;

    const { address: addr, lat, lng } = address;

    const user_agent = navigator.userAgent;
    const platform = detectPlatform(user_agent);
    trackEvent(EV_TRACK_KEY.ev_vehicle_confirm, {
      user_id: user?._id || null,
      userId: user?._id || null,
      platform,
      user_agent,
      city_id: city,
      location: address?.address,
      start_time: dayjs.unix(startTime).format(DEFAULT_DATE_TIME_FORMAT),
      end_time: dayjs.unix(expectedEndTime).format(DEFAULT_DATE_TIME_FORMAT),
      hour_renting: (expectedEndTime - startTime) / 3600,
      vehicle: selectedEVId,
    });
    push(
      {
        pathname: 'checkout',
        query: {
          ...query,
          ...estimateFeeParams,
          rentType,
          address: addr,
          lat,
          lng,
          service_id: selectedEVId,
        },
      },
      undefined,
      {
        shallow: true,
      }
    );
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  const value = React.useMemo(() => {
    return {
      activeStep,
      serviceModels,
      // setServiceModels,
      nextStep,
      backStep,
      resetStep,
      availableEVs,
      estimateFee,
      createContract,
      loading:
        estimatingFeeLoading ||
        loadingServiceModels ||
        creatingContract ||
        loadingServiceModelDetail,
      errors,
      goToCheckoutPage,
      selectedEV,
      apiError,
      estimateFeeError: estimateFeeError?.response?.data ?? null,
      createdContract,
      scrollToTop,
      // setLoadingServiceModels,
      loadingServiceModels,
      openNoService,
      setOpenNoService,
      openDialog,
      setOpenDialog,
      note,
      handleSaveNote,
      resetNote,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeStep,
    serviceModels,
    availableEVs,
    estimatingFeeLoading,
    createContract,
    loadingServiceModels,
    errors,
    selectedEVId,
    apiError,
    estimateFeeError,
    createdContract,
    resetStep,
    openNoService,
    openDialog,
    setOpenDialog,
    note,
    handleSaveNote,
    resetNote,
    // setLoadingServiceModels,
  ]);

  return (
    <NewBookingContext.Provider value={value}>
      {children}
    </NewBookingContext.Provider>
  );
};
